import http, { request } from './http';
import type { AxiosResponse } from 'axios';

/**
 * 企业微信OAuth响应
 */
export interface WeComOAuthResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
  userInfo: {
    userId: number;
    username: string;
    realName: string;
    email?: string;
    phone?: string;
    avatar?: string;
    tenantId: number;
    departmentId?: number;
    /** 与飞书 OAuth 一致，有则前端可直接写入角色，免二次请求 */
    roles?: string[];
    accountMergeInvitePending?: boolean;
    accountMergeInviteToken?: string;
    accountMergeLoginPopup?: boolean;
    postLoginRedirect?: string;
  };
}

/**
 * Token刷新请求
 */
export interface TokenRefreshRequest {
  refreshToken: string;
}

/**
 * Token刷新响应
 */
export interface TokenRefreshResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
}

/**
 * 用户信息
 */
export interface UserInfo {
  userId: number;
  username: string;
  realName: string;
  email?: string;
  phone?: string;
  avatar?: string;
  tenantId: number;
  departmentId?: number;
  /** 登录响应：是否存在待处理跨平台合并邀请 */
  accountMergeInvitePending?: boolean;
  /** 合并页 token，与 /account-merge?token= 联用 */
  accountMergeInviteToken?: string;
  accountMergeLoginPopup?: boolean;
  postLoginRedirect?: string;
}

/**
 * 配置账号登录请求
 */
export interface AccountLoginRequest {
  username: string;
  password: string;
  captchaId: string;
  captchaCode: string;
}

export interface AccountLoginCaptchaResponse {
  captchaId: string;
  imageBase64: string;
  /** 有效时长（秒）；缺省按 5 分钟处理 */
  expiresInSeconds?: number;
}

/**
 * 绑定账号请求
 */
export interface BindAccountRequest {
  username: string;
  password: string;
  wecomUserId: string;
  corpId: string;
}

export type ActiveLoginPlatform = 'feishu' | 'wecom' | 'both';

export type DefaultLoginProvider = 'feishu' | 'wecom';

/**
 * 当前激活的登录平台，由后端 SPRING_PROFILES_ACTIVE 决定；同时含 feishu 与 wecom 时为 both。
 */
export async function getActivePlatform(): Promise<{
  platform: ActiveLoginPlatform;
  defaultProvider?: DefaultLoginProvider;
  implicitLoginEnabled?: boolean;
}> {
  try {
    const res = await request<{ platform?: string; defaultProvider?: string; implicitLoginEnabled?: string }>(
      http.get('/auth/active-platform'),
    );
    const p = (res?.platform || '').toLowerCase();
    const platform: ActiveLoginPlatform =
      p === 'feishu' ? 'feishu' : p === 'both' ? 'both' : 'wecom';
    const dp = (res?.defaultProvider || '').toLowerCase();
    const defaultProvider: DefaultLoginProvider | undefined =
      dp === 'feishu' ? 'feishu' : dp === 'wecom' ? 'wecom' : undefined;
    const implicitLoginEnabled = res?.implicitLoginEnabled === 'true';
    return { platform, defaultProvider, implicitLoginEnabled };
  } catch {
    return { platform: 'wecom' };
  }
}

/**
 * 获取企业微信登录URL
 */
export async function getWeComLoginUrl(corpId?: string, redirectUri?: string): Promise<string> {
  const params = new URLSearchParams();
  if (corpId) params.append('corpId', corpId);
  if (redirectUri) params.append('redirectUri', redirectUri);
  
  const response = await request<{ authUrl: string }>(
    http.get(`/auth/wecom/login?${params.toString()}`)
  );
  return response.authUrl;
}

/**
 * 已授权企业（供扫码登录选择企业，二维码 appid 需为该企业 CorpID）
 */
export interface AuthorizedCorp {
  corpId: string;
  corpName: string;
  canLogin: boolean;
}

/**
 * 获取已授权企业列表（用于登录页选择企业后按企业生成二维码）
 */
export async function getAuthorizedCorps(): Promise<AuthorizedCorp[]> {
  const list = await request<AuthorizedCorp[]>(http.get('/auth/wecom/authorized-corps'));
  return Array.isArray(list) ? list : [];
}

/**
 * 扫码登录：获取二维码URL
 * @param corpId 已授权企业的 CorpID；传入则二维码 appid 为该企业（该企业员工可扫），不传则使用服务商企业（仅服务商本企业员工可扫）
 */
export interface WeComQrCodeUrlResult {
  url: string;
  state: string;
}

export async function getWeComQrCodeUrl(
  corpId?: string,
  postLoginRedirect?: string,
): Promise<WeComQrCodeUrlResult> {
  const params = new URLSearchParams();
  if (corpId) params.set('corpId', corpId);
  if (postLoginRedirect) params.set('postLoginRedirect', postLoginRedirect);
  const qs = params.toString();
  const path = qs ? `/auth/wecom/qrcode-url?${qs}` : '/auth/wecom/qrcode-url';
  return request<WeComQrCodeUrlResult>(http.get(path));
}

/**
 * 轮询扫码登录结果
 */
export async function pollWeComLoginResult(state: string): Promise<WeComOAuthResponse | null> {
  try {
    const res = await http.get<WeComOAuthResponse>(`/auth/wecom/poll?state=${encodeURIComponent(state)}`);
    if (res.status === 204) return null;
    return res.data;
  } catch {
    return null;
  }
}

/**
 * 处理企业微信回调
 */
export async function handleWeComCallback(
  code: string,
  state: string,
  corpId?: string
): Promise<WeComOAuthResponse> {
  const params = new URLSearchParams();
  params.append('code', code);
  params.append('state', state);
  if (corpId) params.append('corpId', corpId);
  
  return request<WeComOAuthResponse>(
    http.get(`/auth/wecom/callback?${params.toString()}`)
  );
}

/**
 * 获取用户信息
 */
export async function getUserInfo(): Promise<UserInfo> {
  return request<UserInfo>(http.get('/auth/wecom/userinfo'));
}

/**
 * 绑定账号
 */
export async function bindAccount(
  request: BindAccountRequest,
  wecomUserId: string,
  corpId: string
): Promise<WeComOAuthResponse> {
  const params = new URLSearchParams();
  params.append('wecomUserId', wecomUserId);
  params.append('corpId', corpId);
  
  return request<WeComOAuthResponse>(
    http.post(`/auth/wecom/bind?${params.toString()}`, request)
  );
}

/**
 * 刷新Token
 */
export async function refreshToken(refreshToken: string): Promise<TokenRefreshResponse> {
  return request<TokenRefreshResponse>(
    http.post('/auth/wecom/refresh', { refreshToken })
  );
}

/**
 * 退出登录
 */
export async function logout(): Promise<void> {
  return request<void>(http.post('/auth/logout'));
}

/**
 * 获取账号登录验证码（SVG Base64）
 */
export async function getAccountLoginCaptcha(): Promise<AccountLoginCaptchaResponse> {
  return request<AccountLoginCaptchaResponse>(http.get('/auth/account-login/captcha'));
}

/**
 * 账号登录（需后端开启 AIPD_IMPLICIT_LOGIN_ENABLED；登录库内已有虚拟帐号）
 */
export async function accountLogin(payload: AccountLoginRequest): Promise<WeComOAuthResponse> {
  return request<WeComOAuthResponse>(http.post('/auth/account-login', payload));
}

// ============================================
// 飞书(Feishu) OAuth 接口
// ============================================

/**
 * 飞书OAuth响应
 */
export interface FeishuOAuthResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
  userInfo: {
    userId: number;
    username: string;
    realName: string;
    email?: string;
    phone?: string;
    avatar?: string;
    tenantId: number;
    departmentId?: number;
    feishuUserId?: string;
    /** 用户角色编码列表（登录时已根据部门映射更新） */
    roles?: string[];
    accountMergeInvitePending?: boolean;
    accountMergeInviteToken?: string;
    accountMergeLoginPopup?: boolean;
    postLoginRedirect?: string;
  };
}

/**
 * 已配置应用（飞书）
 */
export interface AuthorizedApp {
  appId: string;
  appName: string;
  canLogin: boolean;
}

/**
 * 获取飞书登录URL
 */
export async function getFeishuLoginUrl(appId?: string, redirectUri?: string): Promise<string> {
  const params = new URLSearchParams();
  if (appId) params.append('appId', appId);
  if (redirectUri) params.append('redirectUri', redirectUri);

  const response = await request<{ authUrl: string }>(
    http.get(`/auth/feishu/login?${params.toString()}`)
  );
  return response.authUrl;
}

/**
 * 获取已配置应用列表（飞书）
 */
export async function getFeishuAuthorizedApps(): Promise<AuthorizedApp[]> {
  const list = await request<AuthorizedApp[]>(http.get('/auth/feishu/authorized-apps'));
  return Array.isArray(list) ? list : [];
}

/**
 * 扫码登录：获取二维码URL（飞书）
 */
export interface FeishuQrCodeUrlResult {
  url: string;
  state: string;
}

export async function getFeishuQrCodeUrl(
  appId?: string,
  accountMergeToken?: string,
  postLoginRedirect?: string,
): Promise<FeishuQrCodeUrlResult> {
  const params = new URLSearchParams();
  if (appId) params.set('appId', appId);
  if (accountMergeToken) params.set('accountMergeToken', accountMergeToken);
  if (postLoginRedirect) params.set('postLoginRedirect', postLoginRedirect);
  const qs = params.toString();
  const path = qs ? `/auth/feishu/qrcode-url?${qs}` : '/auth/feishu/qrcode-url';
  return request<FeishuQrCodeUrlResult>(http.get(path));
}

/**
 * 轮询扫码登录结果（飞书）
 */
export async function pollFeishuLoginResult(state: string): Promise<FeishuOAuthResponse | null> {
  try {
    const res = await http.get<FeishuOAuthResponse>(`/auth/feishu/poll?state=${encodeURIComponent(state)}`);
    if (res.status === 204) return null;
    return res.data;
  } catch {
    return null;
  }
}

/**
 * 处理飞书回调
 */
export async function handleFeishuCallback(
  code: string,
  state: string
): Promise<FeishuOAuthResponse> {
  const params = new URLSearchParams();
  params.append('code', code);
  params.append('state', state);

  return request<FeishuOAuthResponse>(
    http.get(`/auth/feishu/callback?${params.toString()}&responseType=json`)
  );
}

export interface FeishuJsapiConfigResult {
  appId: string;
  timestamp: number;
  nonceStr: string;
  signature: string;
}

export async function getFeishuJsapiConfig(pageUrl: string, appId?: string): Promise<FeishuJsapiConfigResult> {
  const params = new URLSearchParams();
  params.set('url', pageUrl);
  if (appId) params.set('appId', appId);
  return request<FeishuJsapiConfigResult>(http.get(`/auth/feishu/jsapi-config?${params.toString()}`));
}

export async function feishuH5Login(
  code: string,
  postLoginRedirect?: string,
  appId?: string,
): Promise<FeishuOAuthResponse> {
  return request<FeishuOAuthResponse>(
    http.post('/auth/feishu/h5-login', {
      code,
      postLoginRedirect,
      appId,
    }),
  );
}

export async function getFeishuOAuthAuthUrl(
  postLoginRedirect?: string,
  loginMode?: 'authorize' | 'qrcode',
): Promise<{ authUrl: string; loginMode: string }> {
  const params = new URLSearchParams();
  if (postLoginRedirect) params.set('postLoginRedirect', postLoginRedirect);
  if (loginMode) params.set('loginMode', loginMode);
  const qs = params.toString();
  const path = qs ? `/auth/feishu/auth-url?${qs}` : '/auth/feishu/auth-url';
  return request<{ authUrl: string; loginMode: string }>(http.get(path));
}

/**
 * 获取飞书用户信息
 */
export async function getFeishuUserInfo(): Promise<FeishuOAuthResponse['userInfo']> {
  return request<FeishuOAuthResponse['userInfo']>(http.get('/auth/feishu/userinfo'));
}

/**
 * 飞书退出登录
 */
export async function feishuLogout(): Promise<void> {
  return request<void>(http.post('/auth/feishu/logout'));
}
